import type { DashboardCardType } from 'admin-panel/types/dashboard';

export const DEFAULT_DASHBOARD_CARD_ORDER: DashboardCardType[] = [
  'quick-feedback',
  'green-option',
  'commerce',
  'usage',
];

const DASHBOARD_CARD_TYPES = new Set<DashboardCardType>(
  DEFAULT_DASHBOARD_CARD_ORDER
);

export function dashboardCardOrderStorageKey(hotelIdentifier: string) {
  return `dashboardCardOrder:${hotelIdentifier}`;
}

export function normalizeDashboardCardOrder(
  value: unknown
): DashboardCardType[] {
  if (!Array.isArray(value)) {
    return [...DEFAULT_DASHBOARD_CARD_ORDER];
  }

  const normalized: DashboardCardType[] = [];

  value.forEach((cardType) => {
    if (isDashboardCardType(cardType) && !normalized.includes(cardType)) {
      normalized.push(cardType);
    }
  });

  DEFAULT_DASHBOARD_CARD_ORDER.forEach((cardType) => {
    if (!normalized.includes(cardType)) {
      normalized.push(cardType);
    }
  });

  return normalized;
}

export function parseStoredDashboardCardOrder(
  storedValue: string | null
): DashboardCardType[] {
  if (!storedValue) {
    return [...DEFAULT_DASHBOARD_CARD_ORDER];
  }

  try {
    return normalizeDashboardCardOrder(JSON.parse(storedValue));
  } catch {
    return [...DEFAULT_DASHBOARD_CARD_ORDER];
  }
}

export function mergeVisibleDashboardCardOrder(
  currentOrder: DashboardCardType[],
  nextVisibleOrder: DashboardCardType[],
  visibleCardTypes: DashboardCardType[]
) {
  const normalizedCurrentOrder = normalizeDashboardCardOrder(currentOrder);
  const visibleCardTypeSet = new Set(visibleCardTypes);
  const currentVisibleOrder = normalizedCurrentOrder.filter((cardType) =>
    visibleCardTypeSet.has(cardType)
  );
  const normalizedVisibleOrder = nextVisibleOrder.filter(
    (cardType, index, order) =>
      visibleCardTypeSet.has(cardType) && order.indexOf(cardType) === index
  );

  currentVisibleOrder.forEach((cardType) => {
    if (!normalizedVisibleOrder.includes(cardType)) {
      normalizedVisibleOrder.push(cardType);
    }
  });

  let visibleIndex = 0;

  return normalizedCurrentOrder.map((cardType) => {
    if (!visibleCardTypeSet.has(cardType)) {
      return cardType;
    }

    return normalizedVisibleOrder[visibleIndex++] || cardType;
  });
}

export function isDashboardCardType(
  value: unknown
): value is DashboardCardType {
  return (
    typeof value === 'string' &&
    DASHBOARD_CARD_TYPES.has(value as DashboardCardType)
  );
}
